// The arrears list: one row per tenancy behind on rent, worst first, coloured by how late it is, with the ladder's dots
// and what happens next. Shared by Arrears and a development's Property management → Arrears.
import { CalendarCheck, ChevronRight, Reply } from 'lucide-react';
import { Money } from '../ui';
import { formatDate } from '../../lib/viz';
import { daysColour, STEP_SHORT, type ArrearsRow } from '../../lib/arrears';

/** The list in a surface, with its column heads (from md up). */
export function ArrearsList({ rows, activeId, onOpen }: { rows: ArrearsRow[]; activeId?: string | null; onOpen: (r: ArrearsRow) => void }) {
  return (
    <div className="surface overflow-hidden">
      <div className="hidden grid-cols-[minmax(0,1.3fr)_minmax(0,1.4fr)_110px_minmax(0,1.3fr)_20px] gap-4 border-b border-ink-100 bg-ink-50/60 px-4 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-500 md:grid">
        <span>Flat</span><span>Tenant</span><span className="text-right">Owed</span><span>Ladder</span><span />
      </div>
      <div className="divide-y divide-ink-100">
        {rows.map((r) => <ArrearsRowView key={r.tenancyId} r={r} active={r.tenancyId === activeId} onOpen={() => onOpen(r)} />)}
      </div>
    </div>
  );
}

/** The chip on the right: what happens next on the ladder. */
function NextChip({ r }: { r: ArrearsRow }) {
  if (r.paused) return <Chip colour="var(--color-bronze-500)"><CalendarCheck className="h-3 w-3" />Promised {formatDate(r.promise!.promisedOn)}</Chip>;
  if (r.dueNow) return <Chip colour={daysColour(r.daysOverdue)} strong>{r.nextStepLabel} due</Chip>;
  if (r.lastStepSent >= 4) return <Chip colour="var(--status-neutral)">All reminders sent</Chip>;
  if (r.upcoming) return <Chip colour="var(--status-neutral)">{STEP_SHORT[r.upcoming.step]} {formatDate(r.upcoming.on)}</Chip>;
  return <Chip colour="var(--status-neutral)">Too early to chase</Chip>;
}
function Chip({ colour, strong, children }: { colour: string; strong?: boolean; children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium" style={{ color: colour, background: `color-mix(in srgb, ${colour} ${strong ? 14 : 9}%, var(--color-white))` }}>{children}</span>;
}

export function ArrearsRowView({ r, active, onOpen }: { r: ArrearsRow; active: boolean; onOpen: () => void }) {
  const c = daysColour(r.daysOverdue);
  return (
    <button onClick={onOpen} className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-left transition hover:bg-ink-50/60 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.4fr)_110px_minmax(0,1.3fr)_20px] ${active ? 'bg-bronze-50' : ''}`}
      style={{ boxShadow: `inset 3px 0 0 ${c}` }}>
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold text-ink-900">{r.unitReference ?? r.reference}</span>
        <span className="block truncate text-[11.5px] text-ink-500">{r.propertyName}</span>
      </span>
      <span className="text-right md:hidden">
        <Money minor={r.owedMinor} currency={r.currency} className="block text-[13px] font-semibold text-ink-900" />
        <span className="text-[11.5px] font-medium" style={{ color: c }}>{r.daysOverdue} days</span>
      </span>
      <span className="col-span-2 min-w-0 md:col-span-1">
        <span className="flex items-center gap-1.5 truncate text-[13px] text-ink-800">{r.tenants ?? 'Tenant'}{r.replied && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-px text-[10.5px] font-medium text-emerald-700"><Reply className="h-3 w-3" />Replied</span>}</span>
        <span className="block truncate text-[11.5px] text-ink-400">{r.instalments} month{r.instalments === 1 ? '' : 's'} · since {formatDate(r.oldestDueDate)} · {r.lastStepLabel ? `${r.lastStepLabel} sent ${formatDate(r.lastReminderAt!)}` : 'nothing sent yet'}</span>
      </span>
      <span className="hidden text-right md:block">
        <Money minor={r.owedMinor} currency={r.currency} className="block text-[13px] font-semibold text-ink-900" />
        <span className="text-[11.5px] font-medium" style={{ color: c }}>{r.daysOverdue} days</span>
      </span>
      <span className="col-span-2 flex min-w-0 flex-wrap items-center gap-1.5 md:col-span-1">
        <StepDots r={r} />
        <NextChip r={r} />
        {r.promiseBroken && !r.paused && <Chip colour="var(--status-critical)">Promise missed</Chip>}
      </span>
      <ChevronRight className="hidden h-4 w-4 text-ink-300 md:block" />
    </button>
  );
}

/** Four dots: filled = sent, ring = reached, faint = not reached. */
function StepDots({ r }: { r: ArrearsRow }) {
  return (
    <span className="inline-flex items-center gap-1" title={`Reached: ${STEP_SHORT[r.reachedStep]}; sent: ${r.lastStepLabel ?? 'none'}`}>
      {[1, 2, 3, 4].map((n) => (
        <span key={n} className={`h-2 w-2 rounded-full ${n <= r.lastStepSent ? 'bg-ink-800' : n <= r.reachedStep ? 'border border-ink-500' : 'bg-ink-200'}`} />
      ))}
    </span>
  );
}
